import { identity } from 'es-toolkit/function';
import { useCallback, useRef } from 'react';

export function useTimedAction(globalAction?: () => void, globalSkipIfAlreadyEnqueued?: boolean, globalDelay?: number) {
  //
  const refTimer = useRef<NodeJS.Timeout | number | null>(null);
  const refPromise = useRef(new Promise<void>(identity));

  const isActionQueued = !!refTimer.current;

  const enqueueAction = useCallback(
    (action?: () => void, skipIfAlreadyEnqueued?: boolean, delay?: number): Promise<void> => {
      if (refTimer.current) {
        if (skipIfAlreadyEnqueued ?? globalSkipIfAlreadyEnqueued) return refPromise.current;
        clearTimeout(refTimer.current);
        refTimer.current = null;
      }
      refPromise.current = new Promise<void>((resolve) => {
        const newTimerId = setTimeout(() => {
          void Promise.resolve()
            .then(action ?? globalAction ?? identity)
            .then(resolve);
        }, delay ?? globalDelay);
        refTimer.current = newTimerId;
      });
      return refPromise.current;
    },
    [globalAction, globalDelay, globalSkipIfAlreadyEnqueued]
  );

  const cancelAction = useCallback(() => {
    if (!refTimer.current) return false;
    clearTimeout(refTimer.current);
    refTimer.current = null;
    return true;
  }, []);

  return { enqueueAction, cancelAction, isActionQueued };
}

// ------------------------------------------------------------------------------------------------
// Describes a timed action that can be enqueued to trigger after a set duration and cancelled
// ------------------------------------------------------------------------------------------------

export class TimedAction {
  action: () => void;
  readonly delay: number;
  private _timeoutID: NodeJS.Timeout | null;

  get isQueued(): boolean {
    return this._timeoutID !== null;
  }

  constructor(action: () => void, delay: number) {
    this.action = action;
    this.delay = delay;
    this._timeoutID = null;
  }

  enqueue(force: boolean = false): boolean {
    if (this._timeoutID) {
      if (!force) return false;
      clearTimeout(this._timeoutID);
      this._timeoutID = null;
    }
    this._timeoutID = setTimeout(() => {
      this.action();
      this._timeoutID = null;
    }, this.delay);
    return true;
  }

  enqueueNewAction(newAction: () => void, force: boolean = false): boolean {
    this.action = newAction;
    return this.enqueue(force);
  }

  cancel(): boolean {
    if (!this._timeoutID) return false;
    clearTimeout(this._timeoutID);
    this._timeoutID = null;
    return true;
  }
}
